import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChatCircleText, PaperPlaneRight, Plus, Megaphone, Heart, Flag,
  ArrowLeft, CaretLeft, ShieldStar, ArrowsLeftRight, CheckCircle, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { roleLabel } from "@/lib/roles";
import { ScreenGuard } from "@/components/ScreenGuard";
import { AutoGrowTextarea } from "@/components/AutoGrowTextarea";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
};

function ComposeDialog({ open, onClose, onSent, prefill, user }) {
  const [recipients, setRecipients] = useState(null);
  const [recipientId, setRecipientId] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/inbox/recipients").then(r => setRecipients(r.data)).catch(() => {});
    setBody(prefill?.body || "");
    setRecipientId(prefill?.recipient_id || "");
  }, [open]); // eslint-disable-line

  const send = async () => {
    if (!recipientId || !body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/inbox/threads", {
        recipient_id: recipientId, body: body.trim(),
        lesson_context: prefill?.lesson_context || null,
      });
      toast.success("Message sent");
      onSent(data.thread_id);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" aria-describedby={undefined} data-testid="compose-dialog">
        <DialogHeader><DialogTitle className="font-display">New message</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={recipientId} onValueChange={setRecipientId}>
            <SelectTrigger data-testid="compose-recipient-select"><SelectValue placeholder="Send to…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {(recipients || []).map(r => (
                <SelectItem key={r.id} value={r.id} data-testid={`compose-recipient-${r.id}`}>
                  {r.name} · {roleLabel(r.role) || r.role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {recipients !== null && recipients.length === 0 && (
            <div className="text-xs text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3 py-2" data-testid="compose-empty-hint">
              {user?.role === "school_board_admin"
                ? "No teachers yet — add one under the Users tab in your Operations Panel."
                : "No recipients available yet."}
            </div>
          )}
          {prefill?.lesson_context && (
            <div className="text-xs rounded-lg bg-[#F4F4F5] border border-[#C4C8CE] px-3 py-2 text-[#000000]" data-testid="compose-lesson-context">
              About lesson: <b>{prefill.lesson_context.lesson_title}</b>
            </div>
          )}
          <AutoGrowTextarea data-testid="compose-body-input" value={body} onChange={e => setBody(e.target.value)} minRows={2} maxHeight={220}
            placeholder="Write your message…" className="w-full rounded-xl border border-[#C4C8CE] p-3 text-sm" />
          <button data-testid="compose-send-button" className="btn-primary w-full" disabled={sending || !recipientId || !body.trim()} onClick={send}>
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastDialog({ open, onClose, onSent, user }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [scope, setScope] = useState(user?.role === "teacher" ? "class" : "all");
  const [classes, setClasses] = useState([]);
  const [boards, setBoards] = useState([]);
  const [classId, setClassId] = useState("");
  const [boardId, setBoardId] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (user?.role === "teacher") api.get("/classes").then(r => setClasses(r.data || [])).catch(() => {});
    if (user?.role === "super_admin") api.get("/boards").then(r => setBoards(r.data || [])).catch(() => {});
  }, [open]); // eslint-disable-line

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/inbox/broadcast", {
        scope, class_id: classId || null, board_id: boardId || null,
        subject: subject.trim(), body: body.trim(),
      });
      toast.success(`Broadcast sent to ${data.recipients} recipient${data.recipients === 1 ? "" : "s"}`);
      onSent(data.thread_id);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" aria-describedby={undefined} data-testid="broadcast-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Megaphone size={18} weight="duotone" /> Broadcast</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {user?.role === "super_admin" && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="broadcast-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All students (public + school)</SelectItem>
                <SelectItem value="board">One school board</SelectItem>
              </SelectContent>
            </Select>
          )}
          {scope === "board" && (
            <Select value={boardId} onValueChange={setBoardId}>
              <SelectTrigger data-testid="broadcast-board-select"><SelectValue placeholder="Pick a board…" /></SelectTrigger>
              <SelectContent>{boards.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {user?.role === "teacher" && (
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger data-testid="broadcast-class-select"><SelectValue placeholder="Pick a class…" /></SelectTrigger>
              <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <input data-testid="broadcast-subject-input" value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="Subject" className="w-full rounded-xl border border-[#C4C8CE] p-3 text-sm" />
          <textarea data-testid="broadcast-body-input" value={body} onChange={e => setBody(e.target.value)} rows={4}
            placeholder="Announcement…" className="w-full rounded-xl border border-[#C4C8CE] p-3 text-sm" />
          <button data-testid="broadcast-send-button" className="btn-primary w-full" onClick={send}
            disabled={sending || !subject.trim() || !body.trim() || (scope === "class" && !classId) || (scope === "board" && !boardId)}>
            {sending ? "Sending…" : "Send broadcast"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReassignDialog({ open, onClose, threadId, onDone }) {
  const [staff, setStaff] = useState([]);
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/inbox/recipients").then(r => setStaff(r.data.filter(x => x.kind === "user"))).catch(() => {});
  }, [open]);
  const go = async () => {
    try {
      await api.post(`/sos/threads/${threadId}/reassign`, { user_id: userId, reason: reason.trim() });
      toast.success("Thread reassigned");
      onDone();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm bg-white" aria-describedby={undefined} data-testid="sos-reassign-dialog">
        <DialogHeader><DialogTitle className="font-display">Reassign SOS thread</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="reassign-user-select"><SelectValue placeholder="New owner…" /></SelectTrigger>
            <SelectContent className="max-h-64">
              {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {roleLabel(s.role) || s.role}</SelectItem>)}
            </SelectContent>
          </Select>
          <textarea data-testid="reassign-reason-input" value={reason} onChange={e => setReason(e.target.value)} rows={2}
            placeholder="Reason (audit-logged)" className="w-full rounded-xl border border-[#C4C8CE] p-3 text-sm" />
          <button data-testid="reassign-confirm-button" className="btn-primary w-full" disabled={!userId || !reason.trim()} onClick={go}>
            Reassign — student is not notified
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function InboxPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [threads, setThreads] = useState(null);
  const [summary, setSummary] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [conv, setConv] = useState(null);
  const [reply, setReply] = useState("");
  const [typingNames, setTypingNames] = useState([]);
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const prefill = loc.state?.compose || null;
  const scrollRef = useRef(null);

  const loadThreads = () => api.get("/inbox/threads").then(r => setThreads(r.data)).catch(() => setThreads([]));

  useEffect(() => {
    loadThreads();
    api.get("/inbox/summary").then(r => setSummary(r.data)).catch(() => {});
    if (prefill) setComposeOpen(true);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!activeId) { setConv(null); setTypingNames([]); return; }
    const loadConv = () => api.get(`/inbox/threads/${activeId}`).then(r => { setConv(r.data); }).catch(() => {});
    loadConv();
    loadThreads();
    const t = setInterval(() => { loadConv(); loadThreads(); }, 8000);
    const tt = setInterval(() => {
      api.get(`/inbox/threads/${activeId}/typing`).then(r => setTypingNames(r.data.typing || [])).catch(() => {});
    }, 3000);
    return () => { clearInterval(t); clearInterval(tt); };
  }, [activeId]);

  const lastTypingPing = useRef(0);
  const pingTyping = () => {
    if (!activeId) return;
    const now = Date.now();
    if (now - lastTypingPing.current < 2500) return;
    lastTypingPing.current = now;
    api.post(`/inbox/threads/${activeId}/typing`).catch(() => {});
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [conv?.messages?.length, typingNames.length]);

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/inbox/threads/${activeId}/messages`, { body: reply.trim() });
      setConv(c => ({ ...c, messages: [...c.messages, data] }));
      setReply("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const reportMsg = async (mid) => {
    const reason = window.prompt("Why are you reporting this message? (goes to the Super Admin)");
    if (!reason?.trim()) return;
    try {
      await api.post(`/inbox/messages/${mid}/report`, { reason: reason.trim() });
      toast.success("Reported — the Super Admin has been notified");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openThread = (id) => setActiveId(id);
  const t = conv?.thread;
  const isBroadcastReadOnly = t?.kind === "broadcast" && conv?.messages?.[0]?.sender_id !== conv?.my_id;
  const isSosOwner = t?.kind === "sos" && summary?.is_officer;
  const shownThreads = (threads || []).filter((th) =>
    statusFilter === "active" ? !th.resolved : statusFilter === "resolved" ? th.resolved : true
  );

  const toggleResolved = async () => {
    if (!activeId || !t) return;
    const next = !t.resolved;
    try {
      await api.post(`/inbox/threads/${activeId}/resolve`, { resolved: next });
      setConv((c) => ({ ...c, thread: { ...c.thread, resolved: next } }));
      loadThreads();
      toast.success(next ? "Chat marked resolved" : "Chat reopened");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <ScreenGuard>
    <div className="bg-grain min-h-screen">
      <div className="max-w-6xl mx-auto px-5 py-8">
        <button data-testid="inbox-back-button" className="btn-ghost flex items-center gap-1.5 text-[#000000] hover:text-[#000000] mb-3 font-semibold"
          onClick={() => nav("/dashboard")}>
          <ArrowLeft size={17} weight="bold" /> Back to dashboard
        </button>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-[#000000] font-display">Messages</span>
            <h1 className="text-4xl font-extrabold font-display flex items-center gap-2"><ChatCircleText size={34} weight="duotone" /> Inbox</h1>
          </div>
          <div className="flex gap-2">
            {summary?.can_broadcast && (
              <button data-testid="broadcast-open-button" className="btn-secondary flex items-center gap-1.5" onClick={() => setBroadcastOpen(true)}>
                <Megaphone size={16} weight="bold" /> Broadcast
              </button>
            )}
            <button data-testid="compose-open-button" className="btn-primary flex items-center gap-1.5" onClick={() => setComposeOpen(true)}>
              <Plus size={16} weight="bold" /> New message
            </button>
          </div>
        </motion.div>

        <div className="mt-6 grid md:grid-cols-[320px_1fr] gap-4">
          {/* Thread list */}
          <div className={`rounded-2xl border border-[#C4C8CE] bg-white overflow-hidden ${activeId ? "hidden md:block" : ""}`} data-testid="thread-list">
            <div className="px-3 pt-3 pb-2 border-b border-[#EDEEF0]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full h-8 text-xs" data-testid="inbox-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="inbox-filter-all">Show all</SelectItem>
                  <SelectItem value="active" data-testid="inbox-filter-active">Active chats</SelectItem>
                  <SelectItem value="resolved" data-testid="inbox-filter-resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {threads === null ? (
              <div className="p-5 text-sm text-[#000000]">Loading…</div>
            ) : threads.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#000000]" data-testid="inbox-empty-state">
                No conversations yet. Start one with <b>New message</b>.
              </div>
            ) : shownThreads.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#000000]" data-testid="inbox-filter-empty">
                Nothing matches this filter.
              </div>
            ) : (
              <div className="divide-y divide-[#EDEEF0] max-h-[70vh] overflow-y-auto">
                {shownThreads.map(th => (
                  <button key={th.id} data-testid={`thread-item-${th.id}`} onClick={() => openThread(th.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-[#F9FAFB] cursor-pointer border-0 ${activeId === th.id ? "bg-[#F4F4F5]" : th.resolved ? "bg-transparent opacity-60" : "bg-[rgba(204,255,0,0.22)]"}`}>
                    <div className="flex items-center gap-2">
                      {th.kind === "sos" && <Heart size={14} weight="fill" color="#DC2626" className="shrink-0" />}
                      {th.kind === "broadcast" && <Megaphone size={14} weight="duotone" className="shrink-0" />}
                      <span className={`truncate text-sm ${th.unread ? "font-extrabold" : "font-semibold"}`}>
                        {th.kind === "broadcast" ? th.subject
                          : th.kind === "sos" ? (th.subject || "SOS")
                          : (th.participants[0]?.name || "Conversation")}
                      </span>
                      {th.resolved && <CheckCircle size={13} weight="fill" color="#3F6212" className="shrink-0 ml-auto" data-testid={`thread-resolved-${th.id}`} />}
                      {th.unread && <span className="w-2 h-2 rounded-full bg-[#FF5100] shrink-0 ml-auto" data-testid={`thread-unread-${th.id}`} />}
                    </div>
                    <div className="text-xs text-[#000000] truncate mt-0.5">{th.last_preview}</div>
                    <div className="text-[10px] text-[#000000]">{timeAgo(th.last_message_at)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Conversation */}
          <div className={`rounded-2xl border border-[#C4C8CE] bg-white flex flex-col min-h-[50vh] max-h-[calc(var(--vvh,100dvh)*0.75)] ${!activeId ? "hidden md:flex" : ""}`} data-testid="conversation-pane">
            {!conv ? (
              <div className="flex-1 grid place-items-center text-sm text-[#000000]">Pick a conversation</div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-[#EDEEF0] flex items-center gap-2">
                  <button className="btn-ghost md:hidden" onClick={() => setActiveId(null)} data-testid="conversation-back">
                    <CaretLeft size={18} weight="bold" />
                  </button>
                  {t.kind === "sos" && <Heart size={16} weight="fill" color="#DC2626" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate" data-testid="conversation-title">
                      {t.kind === "broadcast" ? t.subject : t.kind === "sos" ? t.subject : (t.participants[0]?.name || "Conversation")}
                    </div>
                    {t.lesson_context && <div className="text-xs text-[#000000] truncate">Lesson: {t.lesson_context.lesson_title}</div>}
                  </div>
                  <button data-testid="thread-resolve-toggle"
                    className={`btn-ghost text-xs flex items-center gap-1 ${t.resolved ? "text-[#000000]" : "text-[#3F6212]"}`}
                    onClick={toggleResolved}
                    title={t.resolved ? "Reopen this chat" : "Mark this chat resolved"}>
                    {t.resolved ? <><ArrowCounterClockwise size={14} /> Reopen</> : <><CheckCircle size={14} weight="bold" /> Resolve</>}
                  </button>
                  {isSosOwner && (
                    <button data-testid="sos-reassign-open" className="btn-ghost text-xs flex items-center gap-1 text-[#000000]" onClick={() => setReassignOpen(true)}>
                      <ArrowsLeftRight size={14} /> Reassign
                    </button>
                  )}
                </div>
                {t.kind === "sos" && t.envelope && (
                  <div className="mx-4 mt-3 rounded-xl bg-[#FEF2F2] border border-[#FCA5A5] p-3 text-xs text-[#7F1D1D]" data-testid="sos-envelope">
                    <div className="font-bold flex items-center gap-1 mb-1"><ShieldStar size={13} weight="fill" /> SOS envelope {t.ghost && <span className="ml-1 px-1.5 rounded bg-[#DC2626] text-white">{t.ghost_tag} · GHOST</span>}</div>
                    {t.envelope.school_name && <div>School: <b>{t.envelope.school_name}</b>{t.envelope.board_name ? ` · Board: ${t.envelope.board_name}` : ""}</div>}
                    {t.envelope.class_name && <div>Class: <b>{t.envelope.class_name}</b> · Teacher: {t.envelope.teacher_name} {t.envelope.teacher_email ? `(${t.envelope.teacher_email})` : ""}</div>}
                  </div>
                )}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-list">
                  {conv.messages.map(m => {
                    const mine = m.sender_id === conv.my_id;
                    return (
                      <div key={m.id} className={`flex msg-in ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#CCFF00] text-[#000000]" : "bg-[#F4F4F5] text-[#000000]"}`}>
                          {!mine && <div className="text-[10px] font-bold text-[#000000] uppercase tracking-wide">{m.sender_name}</div>}
                          <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-[#000000]">{timeAgo(m.created_at)}</span>
                            {!mine && (
                              <button data-testid={`report-message-${m.id}`} title="Report this message"
                                className="btn-ghost text-[10px] text-[#000000] hover:text-[#DC2626] flex items-center gap-0.5"
                                onClick={() => reportMsg(m.id)}>
                                <Flag size={10} /> report
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {typingNames.length > 0 && (
                    <div className="flex justify-start msg-in" data-testid="typing-indicator">
                      <div className="rounded-2xl px-4 py-2.5 bg-[#F4F4F5] flex items-center gap-2">
                        <span className="typing-dots"><i /><i /><i /></span>
                        <span className="text-xs text-[#000000]">{typingNames.join(", ")} {typingNames.length > 1 ? "are" : "is"} typing…</span>
                      </div>
                    </div>
                  )}
                </div>
                {!isBroadcastReadOnly ? (
                  <div className="p-3 border-t border-[#EDEEF0] flex gap-2">
                    <AutoGrowTextarea data-testid="reply-input" value={reply} onChange={e => { setReply(e.target.value); pingTyping(); }} minRows={1} maxHeight={160}
                      onFocus={(e) => {
                        const el = e.target;
                        setTimeout(() => {
                          scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
                          el.scrollIntoView({ block: "nearest" });
                        }, 300);
                      }}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      placeholder="Write a reply…" className="flex-1 rounded-xl border border-[#C4C8CE] p-3 text-sm" />
                    <button data-testid="reply-send-button" className="btn-primary px-4" disabled={sending || !reply.trim()} onClick={sendReply}>
                      <PaperPlaneRight size={18} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <div className="p-3 border-t border-[#EDEEF0] text-xs text-center text-[#000000]" data-testid="broadcast-readonly-note">
                    This is an announcement — replies are off.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ComposeDialog open={composeOpen} onClose={() => setComposeOpen(false)} prefill={prefill} user={user}
        onSent={(tid) => { loadThreads(); setActiveId(tid); }} />
      <BroadcastDialog open={broadcastOpen} onClose={() => setBroadcastOpen(false)} user={user}
        onSent={(tid) => { loadThreads(); setActiveId(tid); }} />
      <ReassignDialog open={reassignOpen} onClose={() => setReassignOpen(false)} threadId={activeId}
        onDone={() => { loadThreads(); setActiveId(null); }} />
    </div>
    </ScreenGuard>
  );
}
